import { useEffect, useMemo, useState } from "react";

import { IHealthSystem } from "~/api/healthSystem";

import { useHierarchySelected } from "#/features/Hierarchy/hooks/useHierarchySelected";
import { useAppConfigQuery } from "#/features/User/queries";

import { HierarchyModelBase, HierarchyModelView, EHierarchyModelViewType } from "../types";

/**
 * Builds a hierarchical view of IAppConfig.healthSystems by transforming the parent containers
 * into a view of children type allowing the using of the child nodes for regions, markets,
 * facilities and units as is.
 */
export const useHierarchyData = () => {
  const appConfig = useAppConfigQuery();
  const appHealthSystems: IHealthSystem[] = useMemo(
    () => appConfig.data?.healthSystems || [],
    [appConfig.data?.healthSystems],
  );
  const selected = useHierarchySelected();
  const [lastOpened, setLastOpened] = useState<string | undefined>();

  // top is the list of all Health Systems
  const [topView, setTopView] = useState<HierarchyModelView>({
    name: "Health Systems",
    type: EHierarchyModelViewType.healthSystem,
    model: { id: null, name: "Health System", children: appHealthSystems },
    hasNext: true,
  });

  /**
   * After a hierarchy model element is created or updated the IAppConfig is updated and this
   * function will update the name and children references using the selectedId.
   */
  useEffect(() => {
    topView.model.children = appHealthSystems;

    if (topView.next) {
      let view: HierarchyModelView | null = topView;
      while (view?.next) {
        const item = view?.model.children?.find((child) => child.id === view?.selectedId);
        if (item) {
          const next = getNext({ view, item });

          if (next) {
            view.next.name = next.name;
            view.next.model = next.model;
            view.next.parentOptions = next.parentOptions;
            view = view.next;
          }
        } else {
          view = null;
        }
      }
    }

    if (selected.healthSystemId && selected.unitId && selected.unitId !== lastOpened) {
      setLastOpened(selected.unitId);

      // auto open and nav to selected unit
      let view: HierarchyModelView | undefined = topView;
      selected.idsMap.forEach((id) => {
        const item = view?.model.children?.find((child) => child.id === id);
        if (view && id && item) {
          view.selectedId = id;
          view.next = getNext({ view, item });
          view = view.next;
        }
      });
    }

    setTopView({ ...topView });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appHealthSystems, selected]);

  const onClick = (view: HierarchyModelView, item: HierarchyModelBase) => {
    view.selectedId = item.id ?? undefined;
    view.next = getNext({ view, item });
    setTopView({ ...topView });
  };

  const onChange = (updated?: boolean) => {
    if (updated) {
      setTopView({ ...topView });
    }
  };

  return { top: topView, onClick, onChange, isLoading: appConfig.isLoading, selected };
};

/**
 * Get the next level of a given view children's view.
 */
type getNextProps = {
  view: HierarchyModelView;
  item: HierarchyModelBase;
};

const getNext = ({ view, item }: getNextProps): HierarchyModelView | undefined => {
  let next: HierarchyModelView | undefined = undefined;

  switch (view.type) {
    case EHierarchyModelViewType.healthSystem:
      next = {
        name: `${item.name} Regions`,
        type: EHierarchyModelViewType.region,
        model: {
          id: item.id,
          name: "Region",
          children: item.regions,
        },
        hasNext: true,
      };
      break;
    case EHierarchyModelViewType.region:
      next = {
        name: `${item.name} Markets`,
        type: EHierarchyModelViewType.market,
        model: {
          id: item.id,
          name: "Market",
          children: item.markets,
        },
        hasNext: true,
      };
      break;
    case EHierarchyModelViewType.market:
      next = {
        name: `${item.name} Facilities`,
        type: EHierarchyModelViewType.facility,
        model: {
          id: item.id,
          name: "Facility",
          children: item.facilities,
        },
        hasNext: true,
      };
      break;
    case EHierarchyModelViewType.facility:
      next = {
        name: `${item.name} Units`,
        type: EHierarchyModelViewType.unit,
        model: {
          id: item.id,
          name: "Units",
          children: item.units,
        },
        hasNext: false,
      };
      break;
  }

  if (next) {
    next.parentOptions = view.model.children;
  }

  return next;
};
